import { useContextValue } from 'app/shared/hooks/use-context-value';
import React, { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { LDUser } from 'launchdarkly-js-client-sdk';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { useUser } from 'app/shared/contexts/user/user.context';
import { useBuilding } from 'app/shared/contexts/building/building.context';
import { useOpenParams } from 'app/shared/hooks/use-open-params/use-open-params.hook';
import { User } from 'app/data/types/user';

export const LAUNCH_DARKLY_ANONYMOUS_EMAIL = 'anonymous@hqo.co';

const initialLDUser: LDUser = {
  key: LAUNCH_DARKLY_ANONYMOUS_EMAIL,
  anonymous: true,
};

const getLaunchDarklyUser = (user: User | null, buildingUuid: string, appBrand: string): LDUser => {
  const launchDarklyUser: LDUser = initialLDUser;

  if (user?.id && buildingUuid) {
    launchDarklyUser.anonymous = false;
    launchDarklyUser.key = user.email;
    launchDarklyUser.email = user.email;
    launchDarklyUser.custom = {
      buildingUUIDs: [buildingUuid],
      appBrand,
    };
  }

  return launchDarklyUser;
};

interface LaunchDarkly {
  Provider: React.ComponentType;
  testFlag?: boolean;
}

export type FeatureFlags = 'testFlag';

export type FeatureFlagsValues = Record<FeatureFlags, boolean>;

const FeatureFlagsContext = createContext<FeatureFlagsValues>({
  testFlag: false,
});

export const FeatureFlagsProvider = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const [launchDarkly, setLaunchDarkly] = useState<LaunchDarkly | null>(null);
  const { user } = useUser();
  const { building } = useBuilding();
  const { appBrand } = useOpenParams();

  const init = useCallback(
    async (ldUser: LDUser) => {
      const ldProvider = await asyncWithLDProvider({
        clientSideID: process.env.REACT_APP_TENANT_LAUNCH_DARKLY_CLIENT_ID ?? '',
        user: ldUser,
      });
      setLaunchDarkly({ Provider: ldProvider as React.ComponentType });
    },
    [setLaunchDarkly],
  );

  useEffect(() => {
    const launchDarklyUser = getLaunchDarklyUser(user, building?.uuid ?? '', appBrand);

    if (!launchDarkly && !launchDarklyUser.anonymous && building?.uuid && appBrand) {
      init(launchDarklyUser);
    }
  }, [launchDarkly, init, user, building?.uuid, appBrand]);

  const { testFlag = false } = launchDarkly ?? {};

  const serializedFeatureFlags = useMemo(() => ({ testFlag }), [testFlag]);

  return <FeatureFlagsContext.Provider value={serializedFeatureFlags}>{children}</FeatureFlagsContext.Provider>;
};

export const useFeatureFlags = <K extends keyof FeatureFlagsValues | undefined = undefined>(key?: K) =>
  useContextValue(FeatureFlagsContext, key);
